import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { AgentClientInfo } from "shared/AgentAccess";
import { authorizeAgent, getAgentConsentInfo } from "src/CalmLensApi";
import AgentApproval, {
  type AgentApprovalDecision,
} from "src/components/auth/AgentApproval/AgentApproval";
import { agentApprovalError } from "src/components/auth/AgentApproval/AgentApprovalError";
import Callout from "src/components/common/Callout/Callout";
import LoadingIndicator from "src/components/common/LoadingIndicator/LoadingIndicator";
import { usePathname, useSearchParams } from "src/lib/Navigation";
import { useExtracted, useTranslateText } from "#i18n";

// The OAuth authorization endpoint for AI assistants (see the backend's oauthRouter). The backend
// checks the app and its redirect URI before anything is shown or sent back to it.
export const Route = createFileRoute("/_authed/oauth/authorize")({
  component: AuthorizePage,
});

function AuthorizePage() {
  const t = useExtracted();
  const translateText = useTranslateText();
  const pathname = usePathname();
  const search = useSearchParams();
  const clientId = search.get("client_id") ?? "";
  const redirectUri = search.get("redirect_uri") ?? "";
  const state = search.get("state");
  const resource = search.get("resource");
  const scopes = (search.get("scope") ?? "").split(/\s+/).filter(Boolean);
  const supported =
    search.get("response_type") === "code" &&
    search.get("code_challenge_method") === "S256" &&
    Boolean(search.get("code_challenge"));
  const [client, setClient] = useState<AgentClientInfo>();
  const clientName = client?.clientName;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getAgentConsentInfo
      .submit({ body: { clientId, redirectUri, resource } })
      .then(({ data }) => setClient(data))
      .catch((error) =>
        setError(
          agentApprovalError(error, t("This connection request isn't valid."), translateText),
        ),
      );
  }, [clientId, redirectUri, resource, supported, t, translateText]);

  /** Back to the app, once the backend has confirmed it registered this address. */
  const returnToApp = (params: Record<string, string>) => {
    const url = new URL(redirectUri);
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
    if (state) url.searchParams.set("state", state);
    window.location.assign(url.toString());
  };

  const approve = async ({ projectId, scopes }: AgentApprovalDecision) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await authorizeAgent.submit({
        body: {
          clientId,
          redirectUri,
          codeChallenge: search.get("code_challenge") ?? "",
          codeChallengeMethod: "S256",
          state,
          resource,
          projectId,
          scopes,
        },
      });
      window.location.assign(data.redirectUrl);
    } catch (error) {
      setError(
        agentApprovalError(error, t("Unable to connect the app. Try again."), translateText),
      );
      setBusy(false);
    }
  };

  if (!supported || (error && !clientName))
    return (
      <main className="mx-auto max-w-xl px-4 py-16">
        <Callout role="alert" color="danger">
          {error || t("This connection request isn't valid. Start connecting again from the app.")}
        </Callout>
      </main>
    );
  if (!clientName)
    return (
      <main className="mx-auto flex max-w-xl justify-center px-4 py-16">
        <LoadingIndicator aria-label={t("Checking the app")} />
      </main>
    );
  return (
    <AgentApproval
      clientName={clientName}
      redirectTarget={client?.redirectTarget}
      returnTo={`${pathname}?${search.toString()}`}
      allowWrite={!scopes.length || scopes.includes("write")}
      error={error}
      busy={busy}
      onApprove={(decision) => void approve(decision)}
      onDeny={() => returnToApp({ error: "access_denied" })}
    />
  );
}
